import { MapLike, ValidBusinessObject } from '@iotv/iotv-v3-types'
import { Milliseconds } from '../../../../constants'
import {
  DayHour,
  DayNumber,
  daysInLastUTCMonth,
  DEBUG_OVERRIDE,
  MinuteNumber,
  MonthNumber,
  NOT_LAMBDA,
  QuarterIdx,
  TimeBucketType,
  TimeBucketUnits,
} from '../../../../util'
import { HourlyYearBasedArrayDefType, QuarterHourMonthBasedArrayDef, QuarterHourMonthBasedArrayDefType } from '../sharedTypes'
import { DimensionedPeriodizer, DimensionedPeriodizerProps } from './DimensionedPeriodizer'

const debug = DEBUG_OVERRIDE || (NOT_LAMBDA && false)

const className = 'QuarterHourPeriodizer'

export type QuarterHourPeriodizerProps = DimensionedPeriodizerProps & {
  arrayLength?: QuarterHourMonthBasedArrayDefType
  timeBucketDefiniion?: TimeBucketType & {
    unit: typeof TimeBucketUnits.QUARTERHOUR
    width: 1
  }
}

/**
 * @class QuarterHourPeriodizer manages periodization for 1 month of quarter hour peridos
 */
export class QuarterHourPeriodizer extends DimensionedPeriodizer<MapLike<number | null>, QuarterHourMonthBasedArrayDefType> {
  constructor(ob?: QuarterHourPeriodizerProps & ValidBusinessObject) {
    super(ob)
    if (!ob) {
      this.type = className
      this.arrayLengths = QuarterHourMonthBasedArrayDef
      this.initialize()
    }
  }

  timeBucketDefinition: TimeBucketType = {
    unit: TimeBucketUnits.QUARTERHOUR,
    width: 1,
  }

  timeBucketToAccessor = (timeBucketAccessor: [number, MonthNumber, DayNumber, DayHour, MinuteNumber, MinuteNumber]) => {
    const [y, m, d, h, min, s] = timeBucketAccessor
    const thisAccessor: [DayNumber, DayHour, QuarterIdx] = [(d - 1) as DayNumber, h, Math.floor(min / 15) as QuarterIdx]
    return thisAccessor
  }

  periodizerDaysLength = () => daysInLastUTCMonth(this.lastUpdatedTimestamp ?? 0)

  getFirstPossibleTimestamp = () => (this.lastUpdatedTimestamp ?? 0) - this.periodizerDaysLength() * Milliseconds.DAY + 15 * Milliseconds.MINUTE

  accessorToTimestamp = (accessor: number[]): number => {
    const startTimestamp = this.lastUpdatedTimestamp ?? 0
    const currentAccessor = this.timestampToAccessor(startTimestamp)
    const [daysDiff, hoursDiff, quartersDiff] = accessor.map((v, i) => v - currentAccessor[i])

    // handle rollover by when days same hour same quarter more, days same hour more, days more
    let absoluteNegativeDiff: number | undefined
    const relativeDiff = daysDiff * Milliseconds.DAY + hoursDiff * Milliseconds.HOUR + quartersDiff * Milliseconds.MINUTE * 15

    if (relativeDiff > 0) {
      // absolute diff A + B is length of bucket minus how far ahead the accessor is xxxxx  [<---A---xxxxx--B-->]
      absoluteNegativeDiff = -(this.periodizerDaysLength() * Milliseconds.DAY - relativeDiff)
    } else {
      absoluteNegativeDiff = relativeDiff
    }

    const impliedTimestamp = startTimestamp + absoluteNegativeDiff
    return impliedTimestamp
  }

  isValidAccessor = (args: number[]): args is QuarterHourMonthBasedArrayDefType => {
    const isValid =
      args.length === this.arrayLengths?.length &&
      args.every((d, i) => d >= 0 && d < (this.arrayLengths?.[i] ?? 0)) &&
      args[0] <= this.periodizerDaysLength() - 1
    if (!isValid) {
      debug && console.log(`Not valid accessor`, args)
    }
    return isValid
  }
}
